import { Button, Row, Spin } from "antd";
import ErrorToDisplay from "components/error-handling/ErrorToDisplay";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { fetchRaters } from "redux/reducers/CredentialsSlice";
import { CREDENTIAL_MODAL_STATUS } from "utils/Constants";
import { CredentialModalStatusType, StaffUserType } from "utils/Types";
import { common } from "utils/Wording";
import InviteStaffModal from "./modals/InviteStaffModal";
import StaffCard from "./staff-card";
import StaffMemberLimitReachedModal from "./modals/StaffMemberLimitReachedModal";
import StaffDetails from "./staff-details";
import { useTranslation } from "react-i18next";
import style from "./style.module.css";

export const Credentials: React.FC = () => {
  const { t } = useTranslation();
  const maxStaffCount =
    parseInt(useAppSelector(state => state.properties.data.staffCountMax || "0")) || 0;
  const dispatch = useAppDispatch();
  const raters = useAppSelector(state => state.credentials.raters);
  const isRatersLoading = useAppSelector(state => state.credentials.isLoading);
  const isRatersLoadingError = useAppSelector(state => state.credentials.isLoadingError);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [displayStaffMember, setDisplayStaffMember] = useState<StaffUserType>();
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [staffMemberLimitReachedVisible, setStaffMemberLimitReachedVisible] =
    useState<boolean>(false);
  const [addModalStatus, setAddModalStatus] = useState<CredentialModalStatusType>(
    CREDENTIAL_MODAL_STATUS.ADD
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById("skip-main-wrap")?.focus();
  }, []);

  useEffect(() => {
    dispatch(fetchRaters());
  }, []);

  useEffect(() => {
    if (displayStaffMember) {
      setDetailsOpen(true);
    }
  }, [displayStaffMember]);

  const changeAddModalToView = useCallback(() => {
    setAddModalStatus(CREDENTIAL_MODAL_STATUS.VIEW);
  }, [addModalStatus]);

  const closeAddModal = useCallback(() => {
    setIsAddModalOpen(false);
    setAddModalStatus(CREDENTIAL_MODAL_STATUS.ADD);
  }, [isAddModalOpen]);

  const handleInviteStaff = useCallback(() => {
    const currentNoStaff = raters.length;
    if (currentNoStaff === maxStaffCount) {
      setStaffMemberLimitReachedVisible(true);
    } else {
      setIsAddModalOpen(true);
    }
  }, [maxStaffCount, raters.length]);

  return (
    <>
      <Helmet>
        <title>{t("helmet.staffMembers.title")}</title>
        <meta name="description" content={t("helmet.staffMembers.meta") || ""} />
      </Helmet>
      {/* modals */}
      <InviteStaffModal
        isAddModalOpen={isAddModalOpen}
        modalStatus={addModalStatus}
        onClose={closeAddModal}
        changeModalToView={changeAddModalToView}
      />
      <StaffMemberLimitReachedModal
        open={staffMemberLimitReachedVisible}
        onClose={() => setStaffMemberLimitReachedVisible(false)}
      />
      {/* drawer */}
      <StaffDetails
        staff={displayStaffMember}
        open={detailsOpen}
        close={() => setDetailsOpen(false)}
        setDisplayStaffMember={setDisplayStaffMember}
      />

      {isRatersLoadingError ? (
        <ErrorToDisplay title={common.loadingError} />
      ) : isRatersLoading ? (
        <Spin size="large" className="center-content" style={{ height: "50vh" }} />
      ) : (
        <>
          <div className={style["top-container"]}>
            <div>
              <h1 className="display-mid-typo">{t("staffList.heading")}</h1>
              <div className="gap-section">
                <p className="bottom-zero">{t("staffList.description")}</p>
              </div>
            </div>

            <Button type="primary" onClick={handleInviteStaff} disabled={isRatersLoading}>
              {t("staffList.inviteCTA")}
            </Button>
          </div>
          <Row className="text-center">
            {raters?.length > 0 ? (
              <div className={style["staff-card-container"]}>
                {raters.map((rater: any) => (
                  <StaffCard key={rater.email} staff={rater} handleView={setDisplayStaffMember} />
                ))}
              </div>
            ) : (
              <div>
                <p>No staff</p>
              </div>
            )}
          </Row>
        </>
      )}
    </>
  );
};

export default Credentials;
